import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface BookmarkContextType {
  bookmarkedChallenges: Set<string>;
  isBookmarked: (challengeId: string) => boolean;
  toggleBookmark: (challengeId: string) => Promise<void>;
  loading: boolean;
}

const BookmarkContext = createContext<BookmarkContextType | undefined>(undefined);

export const BookmarkProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [bookmarkedChallenges, setBookmarkedChallenges] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchBookmarks();
    } else {
      setBookmarkedChallenges(new Set());
      setLoading(false);
    }
  }, [user]);

  const fetchBookmarks = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("challenge_bookmarks")
        .select("challenge_id")
        .eq("user_id", user.id);

      if (error) throw error;

      setBookmarkedChallenges(new Set(data?.map((b) => b.challenge_id) || []));
    } catch (error) {
      console.error("Failed to fetch bookmarks:", error);
    } finally {
      setLoading(false);
    }
  };

  const isBookmarked = (challengeId: string) => {
    return bookmarkedChallenges.has(challengeId);
  };

  const toggleBookmark = async (challengeId: string) => {
    if (!user) return;

    const isCurrentlyBookmarked = bookmarkedChallenges.has(challengeId);

    // Optimistic update
    const newBookmarks = new Set(bookmarkedChallenges);
    if (isCurrentlyBookmarked) {
      newBookmarks.delete(challengeId);
    } else {
      newBookmarks.add(challengeId);
    }
    setBookmarkedChallenges(newBookmarks);

    try {
      if (isCurrentlyBookmarked) {
        const { error } = await supabase
          .from("challenge_bookmarks")
          .delete()
          .eq("user_id", user.id)
          .eq("challenge_id", challengeId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("challenge_bookmarks")
          .insert({
            user_id: user.id,
            challenge_id: challengeId,
          });

        if (error) throw error;
      }
    } catch (error) {
      // Revert on error
      console.error("Failed to toggle bookmark:", error);
      setBookmarkedChallenges(bookmarkedChallenges);
    }
  };

  return (
    <BookmarkContext.Provider
      value={{ bookmarkedChallenges, isBookmarked, toggleBookmark, loading }}
    >
      {children}
    </BookmarkContext.Provider>
  );
};

export const useBookmarks = () => {
  const context = useContext(BookmarkContext);
  if (context === undefined) {
    throw new Error("useBookmarks must be used within a BookmarkProvider");
  }
  return context;
};
